import { Component, effect, input, linkedSignal, Output, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { debouncedSignal } from '@sona/angular-util-common';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { SonaIconComponent } from '../icon/icon.component';

@Component({
    selector: 'sona-search-bar',
    imports: [
        FormsModule,
        HlmButtonDirective,
        HlmInputDirective,
        SonaIconComponent,
        TranslatePipe
    ],
    template: `
        <sona-icon 
            class="tw-absolute tw-z-10 tw-left-5 tw-inset-y-0 tw-my-auto" 
            name="magnifying-glass"
            size="small" />
        <input
            hlmInput 
            class="tw-bg-white tw-w-full tw-pl-12 tw-pr-12 tw-py-4 tw-border-border/75 tw-shadow-[0_0.125rem_0.25rem_rgba(0,0,0,0.075)]"
            size="xl"
            [placeholder]="placeholder()" 
            [(ngModel)]="search" />

        @if (search()) {
            <button
                hlmBtn
                class="tw-absolute tw-z-10 tw-right-3 tw-inset-y-0 tw-my-auto"
                variant="ghost"
                size="icon-sm"
                [attr.aria-label]="'TARGET.FEATURE_BROWSE.PAGE.SEARCH.CLEAR' | translate"
                (click)="search.set('')">
                <sona-icon name="x" size="small" />
            </button>
        }
    `,
    host: {
        class: 'tw-block tw-relative tw-w-full'
    }
})
export class SonaSearchBarComponent {

    /**
     * The debounce time in milliseconds for search input changes.
     */
    readonly debounce = input(300);
    readonly placeholder = input<string>();
    readonly searchInput = input<string | null>(null, { alias: 'search' });
    readonly searchChange2 = output<string | null>();

    readonly search = linkedSignal(this.searchInput);

    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('searchChange')
    readonly searchOutput = toObservable(this.search).pipe(
        debounceTime(300),
        distinctUntilChanged()
    );

    constructor() {
        const debouncedSearch = debouncedSignal(this.search, this.debounce);

        effect(() => {
            const newValue = debouncedSearch();

            if (newValue === this.searchInput()) {
                return;
            }

            this.searchChange2.emit(newValue);
        });
    }
}
